// Typed API contract consumed by the web app's apiFetch. Each key is
// "METHOD /path" (path params in braces); types tie query/body/response
// together so call sites are fully inferred.

import type {
  AdminInvoicesQueryInput,
  AdminPaymentsQueryInput,
  AdminExpensesQueryInput,
  GstReportQueryInput,
  CreateExpenseInvoiceInput,
  UpdateExpenseInvoiceInput,
  AssignExpenseProjectInput,
  ContractorMonthReportQueryInput,
  AdminTimesheetsQueryInput,
  AssignmentsQuery,
  BulkReviewInput,
  ChaseQueryInput,
  CorrectInvoiceFieldsInput,
  CloseInvoiceShortInput,
  RecordInvoicePaymentInput,
  UpdateInvoicePaymentInput,
  MyInvoicesQueryInput,
  ReportsQueryInput,
  DashboardQuery,
  ReturnInvoiceInput,
  ExplainInvoiceInput,
  ReviewInvoiceInput,
  ApproveInvoiceInput,
  TagInvoiceLinesInput,
  SetInvoiceProjectInput,
  AssignInvoiceProjectInput,
  DiscardInvoiceIntakeInput,
  FileInvoiceIntakeInput,
  FileMyInvoiceIntakeInput,
  InvoiceIntakesQueryInput,
  InvoiceMonthsQuery,
  TimesheetsExportQueryInput,
  CreateAdminInput,
  CreateAssignmentInput,
  CreateClientInput,
  CreateContractorInput,
  CreateProjectInput,
  MyTimesheetQuery,
  MyTimesheetsQuery,
  RecallTimesheetInput,
  RejectTimesheetInput,
  SaveTimesheetInput,
  SetAdminStatusInput,
  SetAssignmentRateInput,
  UpdateAdminInput,
  UpdateAssignmentInput,
  UpdateClientInput,
  UpdateContractorInput,
  UpdateContractorStatusInput,
  UpdateProjectInput,
} from "./schemas.js";
import type { InvoiceNoteDraft } from "./invoice-notes.js";
import type {
  AdminRow,
  AdminWithInvite,
  AssignmentDTO,
  BulkReviewResult,
  ChaseListDTO,
  CsvDownload,
  AssignmentRateDTO,
  AssignmentRow,
  ClientRow,
  ContractorInvoiceCounts,
  ContractorInvoiceSummary,
  ContractorInvoiceDetail,
  ContractorInvoiceRow,
  PaymentRow,
  ExpenseInvoiceRow,
  ExpenseInvoiceCounts,
  ExpenseInvoiceSummary,
  ExpenseVendorOption,
  AssignExpenseProjectResult,
  AssignInvoiceProjectResult,
  ExpenseInvoiceDetail,
  GstReportDTO,
  InvoiceIntakeDTO,
  InvoiceIntakeList,
  InvoiceMonthsDTO,
  MyInvoiceIntakeDTO,
  ProjectDetailDTO,
  PaymentsSummary,
  ContractorRow,
  ContractorWithInvite,
  InviteDTO,
  DashboardDTO,
  DirectoryDashboardDTO,
  MyDashboardDTO,
  MultipartBody,
  MyContractorInvoiceDetail,
  MyContractorInvoiceRow,
  MyTimesheetDTO,
  MyTimesheetRow,
  PageInfo,
  PdfDownload,
  ProjectOption,
  ProjectPickerDTO,
  ProjectRow,
  QueueCounts,
  QueueDetailDTO,
  QueueRow,
  HoursReportDTO,
  ReportsDTO,
  NotificationsDTO,
  SessionUser,
} from "./dto.js";

export type ApiRoutes = {
  /** Client-side logout under MSAL; the server has no session to destroy. */
  "POST /api/auth/logout": { response: { ok: true } };
  /**
   * Runtime MSAL configuration for the SPA (S1.1). `configured: false` when the
   * deployment has not set the Entra env vars yet. `devSignin` enables the
   * dev-only, non-production sign-in used for local dev and E2E.
   */
  "GET /api/auth/config": {
    response:
      | { configured: false; devSignin: boolean }
      | {
          configured: true;
          authority: string;
          clientId: string;
          scopes: string[];
          devSignin: boolean;
        };
  };
  /** Dev-only (non-production): mint a bearer token for an email. */
  "POST /api/auth/dev-token": {
    body: { email: string; name?: string };
    response: { token: string };
  };
  /** Dev-only (non-production): activate an invite without an Entra round-trip. */
  "POST /api/auth/dev-activate": {
    body: { token: string };
    response: { token: string; user: SessionUser };
  };
  "GET /api/me": { response: { user: SessionUser } };

  "GET /api/my/assignments": { response: { assignments: AssignmentDTO[] } };
  "GET /api/my/timesheet": { query: MyTimesheetQuery; response: MyTimesheetDTO };
  "GET /api/my/timesheets": {
    query: MyTimesheetsQuery;
    response: { timesheets: MyTimesheetRow[] };
  };
  "PUT /api/my/timesheet": { body: SaveTimesheetInput; response: MyTimesheetDTO };
  "POST /api/my/timesheet/recall": {
    body: RecallTimesheetInput;
    response: MyTimesheetDTO;
  };
  "GET /api/my/notifications": { response: NotificationsDTO };
  "POST /api/my/notifications/{id}/read": {
    params: { id: string };
    response: NotificationsDTO;
  };
  "POST /api/my/notifications/read-all": { response: NotificationsDTO };

  // --- Contractor invoices (CINV), the contractor's side --------------------
  "GET /api/my/invoices": {
    query: MyInvoicesQueryInput;
    response: { rows: MyContractorInvoiceRow[]; pageInfo: PageInfo };
  };
  /**
   * Upload this month's invoice: `multipart/form-data` with the PDF under `file`
   * and the month under `period`. Call it with `apiUpload`, not `apiFetch`. Answers
   * before the document is read — extraction runs on a queue (CINV-5).
   */
  "POST /api/my/invoices": {
    body: MultipartBody<{ period: "text"; file: "file" }>;
    response: MyContractorInvoiceDetail;
  };
  "GET /api/my/invoices/{id}": {
    params: { id: string };
    response: MyContractorInvoiceDetail;
  };
  /** Replace the PDF on a SUBMITTED or CHANGES_REQUESTED invoice — a new version, same row (CINV-1). */
  "PUT /api/my/invoices/{id}/file": {
    params: { id: string };
    body: MultipartBody<{ file: "file" }>;
    response: MyContractorInvoiceDetail;
  };
  /** Streams the PDF: `apiFile`/`apiDownload`, never `apiFetch`. */
  "GET /api/my/invoices/{id}/file": {
    params: { id: string };
    response: PdfDownload;
  };

  // Admin-only endpoints live under /api/manage, not /api/admin: the Azure
  // Functions host reserves the `admin` route segment for its own management
  // API and silently refuses to register any function that uses it.
  "GET /api/manage/dashboard": { query: DashboardQuery; response: DashboardDTO };
  "GET /api/manage/directory-dashboard": { response: DirectoryDashboardDTO };
  "GET /api/my/dashboard": { response: MyDashboardDTO };
  "GET /api/manage/timesheets": {
    query: AdminTimesheetsQueryInput;
    response: { rows: QueueRow[]; counts: QueueCounts; pageInfo: PageInfo };
  };
  /** The seven entries live here, not on every row of the queue (DATA-7). */
  "GET /api/manage/timesheets/{id}": {
    params: { id: string };
    response: QueueDetailDTO;
  };
  "POST /api/manage/timesheets/{id}/approve": {
    params: { id: string };
    response: { ok: true };
  };
  "POST /api/manage/timesheets/{id}/reject": {
    params: { id: string };
    body: RejectTimesheetInput;
    response: { ok: true };
  };
  /**
   * Week actions deliberately use a different route prefix. A path such as
   * `/timesheets/week/approve` collides with `/timesheets/{id}/approve` in the
   * Azure Functions router, which can treat "week" as a timesheet id.
   */
  "POST /api/manage/timesheet-weeks/approve": {
    body: { ids: string[] };
    response: { ok: true };
  };
  "POST /api/manage/timesheet-weeks/reject": {
    body: { ids: string[]; note: string };
    response: { ok: true };
  };
  /**
   * Bulk review. Both answer 200 even on total failure — the result body is the
   * only record of which weeks committed, and a thrown error discards it.
   */
  "POST /api/manage/timesheet-weeks/bulk-approve": {
    body: BulkReviewInput;
    response: BulkReviewResult;
  };
  /** The compensating half of the Undo toast; window-limited server-side. */
  "POST /api/manage/timesheet-weeks/bulk-unapprove": {
    body: BulkReviewInput;
    response: BulkReviewResult;
  };
  /**
   * One contractor, one client, one month, at day grain — the file an admin
   * forwards to that client. Names, projects, hours and notes; deliberately no
   * email and no money. Streams a file: `apiDownload`, not `apiFetch`.
   */
  "GET /api/manage/exports/contractor-month": {
    query: ContractorMonthReportQueryInput;
    response: CsvDownload;
  };
  /** Streams a file, not JSON — call it with `apiDownload`, not `apiFetch`. */
  "GET /api/manage/exports/timesheets": {
    query: TimesheetsExportQueryInput;
    response: CsvDownload;
  };
  /** Hours only (`HoursReportDTO`) when the reader is a user admin (AUTH-13). */
  "GET /api/manage/reports": {
    query: ReportsQueryInput;
    response: ReportsDTO | HoursReportDTO;
  };
  /** GST paid on incoming invoices by BAS quarter (requirement 15). */
  "GET /api/manage/reports/gst": {
    query: GstReportQueryInput;
    response: GstReportDTO;
  };
  /** Weeks a contractor was engaged for but has not submitted (T4-1). */
  "GET /api/manage/chase": {
    query: ChaseQueryInput;
    response: ChaseListDTO;
  };
  /**
   * Runs the monthly reminder now, instead of waiting for the 25th (R-NT-1).
   * Idempotent per contractor per month, so calling it twice sends once.
   */
  "POST /api/manage/reminders/run": {
    response: { sent: number; skipped: number };
  };

  // --- Contractor invoices (CINV), the admin inbox ----------------------------
  // Every per-invoice action sits *under* `{id}`; no literal segment ever shares
  // that position (see the route-shadowing test).
  "GET /api/manage/invoices": {
    query: AdminInvoicesQueryInput;
    response: {
      rows: ContractorInvoiceRow[];
      counts: ContractorInvoiceCounts;
      summary: ContractorInvoiceSummary;
      pageInfo: PageInfo;
    };
  };
  // --- Expense invoices (EXP) ----------------------------------------------------
  // Every per-expense action sits under `{id}`. The upload has its own path,
  // `manage/expense-uploads`, as the week routes live under `timesheet-weeks/*`:
  // a literal at `{id}`'s position would be shadowed (see the registration test).
  "GET /api/manage/expenses": {
    query: AdminExpensesQueryInput;
    response: {
      rows: ExpenseInvoiceRow[];
      counts: ExpenseInvoiceCounts;
      summary: ExpenseInvoiceSummary;
      vendors: ExpenseVendorOption[];
      pageInfo: PageInfo;
      /** The address expenses can be emailed to (EXP-11); null where no mailbox is read. */
      mailbox: string | null;
    };
  };
  /** Record an expense by hand — every field typed, no document. */
  "POST /api/manage/expenses": { body: CreateExpenseInvoiceInput; response: ExpenseInvoiceDetail };
  /** Record an expense from its document: multipart, the PDF under `file`. `apiUpload`. */
  "POST /api/manage/expense-uploads": {
    body: MultipartBody<{ projectId: "text"; category: "text"; expenseDate: "text"; file: "file" }>;
    response: ExpenseInvoiceDetail;
  };
  /** File several expenses under one project (EXP-8): a sibling path, as `{id}` would shadow a literal. */
  "POST /api/manage/expense-projects": {
    body: AssignExpenseProjectInput;
    response: AssignExpenseProjectResult;
  };
  "GET /api/manage/expenses/{id}": { params: { id: string }; response: ExpenseInvoiceDetail };
  /** The expense's own attributes: project, category, date, note. */
  "PATCH /api/manage/expenses/{id}": {
    params: { id: string };
    body: UpdateExpenseInvoiceInput;
    response: ExpenseInvoiceDetail;
  };
  /** The document's fields, as an overlay — the same schema as a contractor invoice's. */
  "PATCH /api/manage/expenses/{id}/fields": {
    params: { id: string };
    body: CorrectInvoiceFieldsInput;
    response: ExpenseInvoiceDetail;
  };
  "GET /api/manage/expenses/{id}/file": { params: { id: string }; response: PdfDownload };
  /** Attach a document to a hand-entered expense, or replace one. */
  "PUT /api/manage/expenses/{id}/file": {
    params: { id: string };
    body: MultipartBody<{ file: "file" }>;
    response: ExpenseInvoiceDetail;
  };
  /** Void: out of every total. Terminal, and the trail stays — until it is deleted for good (EXP-12). */
  "POST /api/manage/expenses/{id}/void": {
    params: { id: string };
    body: ReturnInvoiceInput;
    response: ExpenseInvoiceDetail;
  };
  /** Delete a void expense for good, document and trail with it (EXP-12). Refused unless VOID. */
  "DELETE /api/manage/expenses/{id}": {
    params: { id: string };
    query: { version: number };
    response: { ok: true };
  };
  "POST /api/manage/expenses/{id}/rerun-extraction": {
    params: { id: string };
    body: ReviewInvoiceInput;
    response: { queued: boolean; expense: ExpenseInvoiceDetail };
  };
  /** One project for its page: the list's row and its invoice lines' sums (CINV-20). */
  "GET /api/manage/projects/{id}": {
    params: { id: string };
    response: ProjectDetailDTO;
  };
  /** The payment history: PAID invoices by the day they were paid, summed per currency. */
  "GET /api/manage/payments": {
    query: AdminPaymentsQueryInput;
    response: { rows: PaymentRow[]; summary: PaymentsSummary; pageInfo: PageInfo };
  };
  /**
   * Upload a contractor's invoice on their behalf: `multipart/form-data` with the
   * PDF under `file`, the month under `period` and whose it is under
   * `contractorId`. `apiUpload`, not `apiFetch`. Every rule of the contractor's
   * own upload applies; the history records the admin as the uploader.
   */
  "POST /api/manage/invoices": {
    body: MultipartBody<{ contractorId: "text"; period: "text"; file: "file" }>;
    response: ContractorInvoiceDetail;
  };
  "GET /api/manage/invoices/{id}": {
    params: { id: string };
    response: ContractorInvoiceDetail;
  };
  /**
   * One contractor's invoiced months (CINV-1) — what a month picker leaves out.
   * Its own path: under `manage/invoices` it would sit at `{id}`'s position.
   */
  "GET /api/manage/invoice-months": { query: InvoiceMonthsQuery; response: InvoiceMonthsDTO };
  // --- Invoice intake (CINV-21): PDFs dropped before anyone says whose ---------
  /** Drop a PDF on the inbox: multipart, the PDF under `file`. `apiUpload`. */
  "POST /api/manage/invoice-intakes": {
    body: MultipartBody<{ file: "file" }>;
    response: InvoiceIntakeDTO;
  };
  /** Every intake waiting to be filed, or the given batch (`ids`) whatever its status. */
  "GET /api/manage/invoice-intakes": { query: InvoiceIntakesQueryInput; response: InvoiceIntakeList };
  "GET /api/manage/invoice-intakes/{id}/file": { params: { id: string }; response: PdfDownload };
  /** File it: create the contractor's invoice for that month from this intake. */
  "POST /api/manage/invoice-intakes/{id}/invoice": {
    params: { id: string };
    body: FileInvoiceIntakeInput;
    response: { invoiceId: string; intake: InvoiceIntakeDTO };
  };
  // --- A contractor's own intakes (CINV-21) ---------------------------------
  "POST /api/my/invoice-intakes": { body: MultipartBody<{ file: "file" }>; response: MyInvoiceIntakeDTO };
  /** Their intakes waiting to be filed, or the given batch (`ids`) whatever its status. */
  "GET /api/my/invoice-intakes": { query: InvoiceIntakesQueryInput; response: { rows: MyInvoiceIntakeDTO[]; pendingCount: number } };
  "GET /api/my/invoice-intakes/{id}/file": { params: { id: string }; response: PdfDownload };
  "POST /api/my/invoice-intakes/{id}/invoice": {
    params: { id: string };
    body: FileMyInvoiceIntakeInput;
    response: { invoiceId: string; intake: MyInvoiceIntakeDTO };
  };
  "POST /api/my/invoice-intakes/{id}/discard": {
    params: { id: string };
    body: DiscardInvoiceIntakeInput;
    response: MyInvoiceIntakeDTO;
  };
  "POST /api/manage/invoice-intakes/{id}/discard": {
    params: { id: string };
    body: DiscardInvoiceIntakeInput;
    response: InvoiceIntakeDTO;
  };
  /** Streams the PDF for the preview pane: `apiFile`, never `apiFetch`. */
  "GET /api/manage/invoices/{id}/file": {
    params: { id: string };
    response: PdfDownload;
  };
  /** Correct what the model read (CINV-7). Re-validates and returns the fresh detail. */
  "PATCH /api/manage/invoices/{id}/fields": {
    params: { id: string };
    body: CorrectInvoiceFieldsInput;
    response: ContractorInvoiceDetail;
  };
  /** Approve for payment. Needs a total; the engine's verdict is advisory (CINV-6, CINV-16). */
  /** Tag invoice lines to projects, or clear tags (CINV-20). Carries the version read. */
  "PATCH /api/manage/invoices/{id}/line-projects": {
    params: { id: string };
    body: TagInvoiceLinesInput;
    response: ContractorInvoiceDetail;
  };
  /** File the whole invoice under a project, or take it off (CINV-35). Any status but rejected; carries the version read. */
  "PATCH /api/manage/invoices/{id}/project": {
    params: { id: string };
    body: SetInvoiceProjectInput;
    response: ContractorInvoiceDetail;
  };
  /** File several invoices under one project (CINV-35): a sibling path, as `{id}` would shadow a literal. */
  "POST /api/manage/invoice-projects": {
    body: AssignInvoiceProjectInput;
    response: AssignInvoiceProjectResult;
  };
  "POST /api/manage/invoices/{id}/approve": {
    params: { id: string };
    body: ApproveInvoiceInput;
    response: ContractorInvoiceDetail;
  };
  /** Return to the contractor with a note; they upload a corrected file. */
  "POST /api/manage/invoices/{id}/request-correction": {
    params: { id: string };
    body: ReturnInvoiceInput;
    response: ContractorInvoiceDetail;
  };
  /** Terminal. The month is free for a fresh upload. */
  "POST /api/manage/invoices/{id}/reject": {
    params: { id: string };
    body: ReturnInvoiceInput;
    response: ContractorInvoiceDetail;
  };
  /** Say why the flags are fine (CINV-27); replaces any earlier note. Not a rejected invoice. */
  "POST /api/manage/invoices/{id}/explanation": {
    params: { id: string };
    body: ExplainInvoiceInput;
    response: ContractorInvoiceDetail;
  };
  /** A drafted correction note (CINV-33): the model's when it can, else the template. Saves nothing. */
  "POST /api/manage/invoices/{id}/correction-draft": {
    params: { id: string };
    response: InvoiceNoteDraft;
  };
  /** The likeliest reason the flags are fine (CINV-33), usually a preset. Saves nothing. */
  "POST /api/manage/invoices/{id}/explanation-draft": {
    params: { id: string };
    response: InvoiceNoteDraft;
  };
  /** Take the explanation back; the flags show again. */
  "DELETE /api/manage/invoices/{id}/explanation": {
    params: { id: string };
    query: { version: number };
    response: ContractorInvoiceDetail;
  };
  /**
   * Record a payment — the balance or part of it (CINV-23). Never more than the
   * balance; the status follows: PART_PAID, or PAID once nothing is left.
   */
  "POST /api/manage/invoices/{id}/payments": {
    params: { id: string };
    body: RecordInvoicePaymentInput;
    response: ContractorInvoiceDetail;
  };
  /** Correct a recorded payment, at any time; the status follows the new sum. */
  "PATCH /api/manage/invoices/{id}/payments/{paymentId}": {
    params: { id: string; paymentId: string };
    body: UpdateInvoicePaymentInput;
    response: ContractorInvoiceDetail;
  };
  /** Remove a payment entered by mistake. The trail keeps what it said. */
  "DELETE /api/manage/invoices/{id}/payments/{paymentId}": {
    params: { id: string; paymentId: string };
    query: { version: number };
    response: ContractorInvoiceDetail;
  };
  /** Close as paid with a balance left, and why — the contractor is told. */
  "POST /api/manage/invoices/{id}/close-short": {
    params: { id: string };
    body: CloseInvoiceShortInput;
    response: ContractorInvoiceDetail;
  };
  /** Undo a short close: the invoice is PART_PAID or APPROVED again, by its payments. */
  "POST /api/manage/invoices/{id}/reopen": {
    params: { id: string };
    body: ReviewInvoiceInput;
    response: ContractorInvoiceDetail;
  };
  /**
   * Read the PDF again. `queued: false` means the job never reached the queue —
   * the row already says FAILED, and the admin can type the fields in instead.
   */
  "POST /api/manage/invoices/{id}/rerun-extraction": {
    params: { id: string };
    body: ReviewInvoiceInput;
    response: { queued: boolean; invoice: ContractorInvoiceDetail };
  };

  "GET /api/manage/contractors": {
    response: { contractors: ContractorRow[]; projects: ProjectOption[] };
  };
  /** Invites a contractor (S2.1): a pending user plus a one-time invite link. */
  "POST /api/manage/contractors": {
    body: CreateContractorInput;
    response: ContractorWithInvite;
  };
  /** Deactivating cuts off access on the contractor's next request (S2.3). */
  "POST /api/manage/contractors/{id}/status": {
    params: { id: string };
    body: UpdateContractorStatusInput;
    response: { contractor: ContractorRow };
  };

  // Admins manage admins. Every route below is `withAuth(["ADMIN"])` like the rest
  // of `manage/*`, which is the whole of "only an admin can add an admin".
  "GET /api/manage/admins": { response: { admins: AdminRow[] } };
  /** Invites an admin: the contractor invite flow, minus the work. */
  "POST /api/manage/admins": { body: CreateAdminInput; response: AdminWithInvite };
  "PATCH /api/manage/admins/{id}": {
    params: { id: string };
    body: UpdateAdminInput;
    response: { admin: AdminRow };
  };
  /** Refused for yourself, and for the last active admin — see `setAdminStatus`. */
  "POST /api/manage/admins/{id}/status": {
    params: { id: string };
    body: SetAdminStatusInput;
    response: { admin: AdminRow };
  };

  /** Pending invites, to chase, re-send, or revoke (S2.1). */
  "GET /api/manage/invites": { response: { invites: InviteDTO[] } };
  "POST /api/manage/invites/{id}/resend": {
    params: { id: string };
    response: {
      invite: InviteDTO;
      inviteLink: string;
      emailed: boolean;
      provisioned: boolean;
    };
  };
  "POST /api/manage/invites/{id}/revoke": {
    params: { id: string };
    response: { invite: InviteDTO };
  };

  /**
   * Activate an invite on first Entra sign-in (S2.2). Sent with a verified Entra
   * bearer token; binds on the token, verifies on the email.
   */
  "POST /api/invite/activate": {
    body: { token: string };
    response: { user: SessionUser };
  };

  /** Correct a record. Archiving is a status change, never a delete. */
  "PATCH /api/manage/contractors/{id}": {
    params: { id: string };
    body: UpdateContractorInput;
    response: { contractor: ContractorRow };
  };

  "GET /api/manage/clients": { response: { clients: ClientRow[] } };
  "POST /api/manage/clients": {
    body: CreateClientInput;
    response: { client: ClientRow };
  };
  "PATCH /api/manage/clients/{id}": {
    params: { id: string };
    body: UpdateClientInput;
    response: { client: ClientRow };
  };
  "GET /api/manage/projects": { response: { projects: ProjectRow[] } };
  /** Projects to assign work to, with no money on them — a user admin reads it (AUTH-13). */
  "GET /api/manage/project-picker": { response: ProjectPickerDTO };
  "POST /api/manage/projects": {
    body: CreateProjectInput;
    response: { project: ProjectRow };
  };
  "PATCH /api/manage/projects/{id}": {
    params: { id: string };
    body: UpdateProjectInput;
    response: { project: ProjectRow };
  };

  "GET /api/manage/assignments": {
    query: AssignmentsQuery;
    response: { assignments: AssignmentRow[] };
  };
  "POST /api/manage/assignments": {
    body: CreateAssignmentInput;
    response: { assignment: AssignmentRow };
  };
  "PATCH /api/manage/assignments/{id}": {
    params: { id: string };
    body: UpdateAssignmentInput;
    response: { assignment: AssignmentRow };
  };
  /** Only ever succeeds when no timesheet was filed against it — see the service. */
  "DELETE /api/manage/assignments/{id}": {
    params: { id: string };
    response: { ok: true };
  };

  /** The rate history, newest window first (RATE-1). */
  "GET /api/manage/assignments/{id}/rates": {
    params: { id: string };
    response: { rates: AssignmentRateDTO[] };
  };
  /**
   * Change the rate **from a date** — the only way to change one. Weeks already
   * approved keep the rate they were approved against and do not move (RATE-6).
   */
  "POST /api/manage/assignments/{id}/rates": {
    params: { id: string };
    body: SetAssignmentRateInput;
    response: { rates: AssignmentRateDTO[]; assignment: AssignmentRow };
  };

  "GET /api/health": { response: { ok: true } };
};

export type RouteKey = keyof ApiRoutes;

/**
 * The same route keys, as a value.
 *
 * `ApiRoutes` is a type, so nothing can enumerate it at runtime — and the API
 * registers its routes by side effect (`app.http` in `api/src/functions/`, each
 * file imported by `api/src/main.ts`). Without a runtime list there is no way to
 * assert the two agree, which is how `POST /api/invite/activate` came to be
 * declared here, handled in `api/src/functions/invite.ts`, called by the SPA,
 * and registered nowhere.
 *
 * `satisfies Record<RouteKey, true>` is what keeps this honest, and it checks
 * *both* directions: a key missing from this map fails to satisfy the record, and
 * a key that is not in `ApiRoutes` is an excess property. So the duplication
 * cannot drift — it is a compile error, not a convention.
 */
const ROUTE_KEYS = {
  "DELETE /api/manage/assignments/{id}": true,
  "GET /api/auth/config": true,
  "GET /api/health": true,
  "GET /api/manage/admins": true,
  "GET /api/manage/assignments": true,
  "GET /api/manage/assignments/{id}/rates": true,
  "GET /api/manage/chase": true,
  "GET /api/manage/clients": true,
  "GET /api/manage/contractors": true,
  "GET /api/manage/dashboard": true,
  "GET /api/manage/directory-dashboard": true,
  "GET /api/my/dashboard": true,
  "GET /api/manage/exports/contractor-month": true,
  "GET /api/manage/exports/timesheets": true,
  "GET /api/manage/invites": true,
  "GET /api/manage/invoices": true,
  "GET /api/manage/invoices/{id}": true,
  "GET /api/manage/invoices/{id}/file": true,
  "GET /api/manage/invoice-months": true,
  "GET /api/manage/invoice-intakes": true,
  "GET /api/manage/invoice-intakes/{id}/file": true,
  "POST /api/manage/invoice-intakes": true,
  "POST /api/manage/invoice-projects": true,
  "POST /api/manage/invoice-intakes/{id}/invoice": true,
  "POST /api/manage/invoice-intakes/{id}/discard": true,
  "POST /api/my/invoice-intakes": true,
  "GET /api/my/invoice-intakes": true,
  "GET /api/my/invoice-intakes/{id}/file": true,
  "POST /api/my/invoice-intakes/{id}/invoice": true,
  "POST /api/my/invoice-intakes/{id}/discard": true,
  "DELETE /api/manage/expenses/{id}": true,
  "GET /api/manage/expenses": true,
  "GET /api/manage/expenses/{id}": true,
  "GET /api/manage/expenses/{id}/file": true,
  "PATCH /api/manage/expenses/{id}": true,
  "PATCH /api/manage/expenses/{id}/fields": true,
  "POST /api/manage/expense-uploads": true,
  "POST /api/manage/expense-projects": true,
  "POST /api/manage/expenses": true,
  "POST /api/manage/expenses/{id}/rerun-extraction": true,
  "POST /api/manage/expenses/{id}/void": true,
  "PUT /api/manage/expenses/{id}/file": true,
  "GET /api/manage/payments": true,
  "GET /api/manage/projects/{id}": true,
  "GET /api/manage/projects": true,
  "GET /api/manage/project-picker": true,
  "GET /api/manage/reports": true,
  "GET /api/manage/reports/gst": true,
  "GET /api/manage/timesheets": true,
  "GET /api/manage/timesheets/{id}": true,
  "GET /api/me": true,
  "GET /api/my/assignments": true,
  "GET /api/my/invoices": true,
  "GET /api/my/invoices/{id}": true,
  "GET /api/my/invoices/{id}/file": true,
  "GET /api/my/notifications": true,
  "GET /api/my/timesheet": true,
  "GET /api/my/timesheets": true,
  "PATCH /api/manage/admins/{id}": true,
  "PATCH /api/manage/assignments/{id}": true,
  "PATCH /api/manage/clients/{id}": true,
  "PATCH /api/manage/contractors/{id}": true,
  "PATCH /api/manage/invoices/{id}/fields": true,
  "PATCH /api/manage/invoices/{id}/line-projects": true,
  "PATCH /api/manage/invoices/{id}/project": true,
  "PATCH /api/manage/projects/{id}": true,
  "POST /api/auth/dev-activate": true,
  "POST /api/auth/dev-token": true,
  "POST /api/auth/logout": true,
  "POST /api/invite/activate": true,
  "POST /api/manage/admins": true,
  "POST /api/manage/admins/{id}/status": true,
  "POST /api/manage/assignments": true,
  "POST /api/manage/assignments/{id}/rates": true,
  "POST /api/manage/clients": true,
  "POST /api/manage/contractors": true,
  "POST /api/manage/contractors/{id}/status": true,
  "POST /api/manage/invites/{id}/resend": true,
  "POST /api/manage/invites/{id}/revoke": true,
  "POST /api/manage/invoices": true,
  "POST /api/manage/invoices/{id}/approve": true,
  "POST /api/manage/invoices/{id}/payments": true,
  "PATCH /api/manage/invoices/{id}/payments/{paymentId}": true,
  "DELETE /api/manage/invoices/{id}/payments/{paymentId}": true,
  "POST /api/manage/invoices/{id}/close-short": true,
  "POST /api/manage/invoices/{id}/explanation": true,
  "POST /api/manage/invoices/{id}/correction-draft": true,
  "POST /api/manage/invoices/{id}/explanation-draft": true,
  "DELETE /api/manage/invoices/{id}/explanation": true,
  "POST /api/manage/invoices/{id}/reopen": true,
  "POST /api/manage/invoices/{id}/reject": true,
  "POST /api/manage/invoices/{id}/request-correction": true,
  "POST /api/manage/invoices/{id}/rerun-extraction": true,
  "POST /api/manage/projects": true,
  "POST /api/manage/reminders/run": true,
  "POST /api/manage/timesheet-weeks/approve": true,
  "POST /api/manage/timesheet-weeks/bulk-approve": true,
  "POST /api/manage/timesheet-weeks/bulk-unapprove": true,
  "POST /api/manage/timesheet-weeks/reject": true,
  "POST /api/manage/timesheets/{id}/approve": true,
  "POST /api/manage/timesheets/{id}/reject": true,
  "POST /api/my/invoices": true,
  "POST /api/my/notifications/read-all": true,
  "POST /api/my/notifications/{id}/read": true,
  "POST /api/my/timesheet/recall": true,
  "PUT /api/my/invoices/{id}/file": true,
  "PUT /api/my/timesheet": true,
} satisfies Record<RouteKey, true>;

export const API_ROUTE_KEYS = Object.keys(ROUTE_KEYS) as RouteKey[];

type RouteSpec<K extends RouteKey> = ApiRoutes[K];

export type RouteQuery<K extends RouteKey> =
  RouteSpec<K> extends { query: infer Q } ? Q : never;
export type RouteBody<K extends RouteKey> =
  RouteSpec<K> extends { body: infer B } ? B : never;
export type RouteParams<K extends RouteKey> =
  RouteSpec<K> extends {
    params: infer P;
  }
    ? P
    : never;
export type RouteResponse<K extends RouteKey> = RouteSpec<K>["response"];

/** "POST /api/x/{id}/y" → { method: "POST", path: "/api/x/{id}/y" } */
export function parseRouteKey(key: RouteKey): { method: string; path: string } {
  const space = key.indexOf(" ");
  return { method: key.slice(0, space), path: key.slice(space + 1) };
}

/** Substitute {param} placeholders. */
export function buildPath(path: string, params?: Record<string, string>): string {
  if (!params) return path;
  return path.replace(/\{(\w+)\}/g, (_, name: string) => {
    const v = params[name];
    if (v === undefined) throw new Error(`Missing path param: ${name}`);
    return encodeURIComponent(v);
  });
}
